import {useEffect, useState} from 'react';
import {assignProps, animate, dynamic, isServer, toIntent} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {css} from '../css';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {BOTTOM_SHEET_ANIMATION_EASING} from '../components/bottom-sheet/BottomSheet.constants';

const visualViewport = !isServer ? window.visualViewport : undefined;

function getVisualViewportSize() {
  return {
    // eslint-disable-next-line @sail/ui-no-reflow
    width: visualViewport?.width || window.innerWidth,
    // eslint-disable-next-line @sail/ui-no-reflow
    height: visualViewport?.height || window.innerHeight,
    // eslint-disable-next-line @sail/ui-no-reflow
    offsetTop: visualViewport?.offsetTop || 0,
  };
}

// Use visualViewport api to track viewport available height when iOS virtual keyboard is open
function useVisualViewportArea() {
  const [size, setSize] = useState(() =>
    isServer ? {width: 0, height: 0, offsetTop: 0} : getVisualViewportSize(),
  );

  useEffect(() => {
    const onResize = () => {
      setSize(getVisualViewportSize());
    };

    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);

    return () => {
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);

  return size;
}

function useWindowInnerHeight() {
  const [height, setHeight] = useState(
    // eslint-disable-next-line @sail/ui-no-reflow
    isServer ? 0 : window.innerHeight,
  );
  useEffect(() => {
    const onResize = () => {
      // eslint-disable-next-line @sail/ui-no-reflow
      setHeight(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return height;
}

export type BottomSheetVariant = 'bottom-sheet' | 'fullscreen-sheet';

/**
 * The way to get the exact keyboard height is:
 * window.innerHeight (height of the screen) - height (the height of the viewport)
 *
 * offsetTop is the panning on the visual viewport, which can happen automatically when the keyboard opens, or manually by the user zooming in.
 *
 * If we are in an iframe (Connect) we set this as 0px.
 * Currently, the safe-area-insets-* env vars are not consistently applied to iframes across browsers: https://github.com/w3c/csswg-drafts/issues/4670
 * So Connect set the padding themselves: https://git.corp.stripe.com/stripe-internal/mint/blob/master/pay-server/frontend/connect/submerchant-surfaces/src/ui-layer-frame/utils/MobileUIOverrides.tsx#L74
 */
export const viewportAndKeyboardHeightStyles = toIntent([
  dynamic(() => {
    const isConnect = isServer || window.parent !== window;

    return [
      css({
        '--viewport-height': '100vh',
        '@supports (height: 100dvh)': {
          '--viewport-height': '100dvh',
        },
        '--bottom-sheet-viewport-height-without-margins-and-safe-areas':
          'calc(var(--viewport-height) - var(--sail-layer-margin-top, 0px) - var(--sail-layer-margin-bottom, 0px) - env(safe-area-inset-bottom, 0px) - env(safe-area-inset-top, 0px))',
      }),
      isConnect
        ? css({})
        : css({
            paddingBottom:
              'calc(var(--bottom-sheet-safe-bottom-offset) - var(--sail-layer-margin-bottom, 0px))',
          }),
    ];
  }),
  assignProps(() => {
    // eslint-disable-next-line @sail/ui-no-reflow
    const {height, offsetTop} = useVisualViewportArea();
    const windowInnerHeight = useWindowInnerHeight();

    return {
      style: {
        ['--bottom-sheet-safe-bottom-offset' as string]: `max(${Math.max(
          windowInnerHeight - height - offsetTop,
          0,
        )}px, env(safe-area-inset-bottom, 0px))`,
      },
    };
  }),
]);

/**
 * Bottom sheets are visually very different to their desktop counterparts.
 * When styling overlays, developers usually only consider the desktop version.
 * Therefore, we need to reset some styles to ensure the bottom sheet doesn't break due to the desktop customizations.
 *
 * This could be done in a more elegant way, for example having a whitelist of CSS properties allowed to be inherited, but that's not currently supported by @sail/engine.
 */
const resetStyles: React.CSSProperties = {
  maxWidth: '100vw',
  minWidth: '100vw',
  width: 'unset',
  position: 'fixed',
  transform: 'unset',
  opacity: 'unset',
  aspectRatio: 'unset',
  border: 'unset',
  borderRadius: 'unset',
  boxShadow: 'unset',
  margin: 'unset',
};

/**
 * When styling overlays, developers usually only consider the desktop version.
 * Therefore, we need to reset some styles to ensure the bottom sheet doesn't break due to the desktop customizations.
 *
 * This could be done in a more elegant way, for example having a whitelist of CSS properties allowed to be inherited, but that's not currently supported by @sail/engine.
 */
export const popoverContentWrapperResetStyles: React.CSSProperties = {
  ...resetStyles,
  maxWidth: '100%',
  minWidth: '100%',
  position: 'relative',
};

const bottomSheetStyles: React.CSSProperties = {
  ['--bottom-sheet-max-height' as string]:
    'calc(var(--bottom-sheet-viewport-height-without-margins-and-safe-areas) - 40px)',
  bottom: 'var(--sail-layer-margin-bottom, 0px)',
  top: 'initial',
  right: 'initial',
  left: 'initial',
  borderTopLeftRadius: $['space.150'],
  borderTopRightRadius: $['space.150'],
  minHeight:
    'max(calc(var(--bottom-sheet-viewport-height-without-margins-and-safe-areas) * 0.25), var(--close-confirmation-dialog-height, 0px))',
  height: 'var(--popover-bottom-sheet-always-use-max-height, unset)',
  maxHeight: 'var(--bottom-sheet-max-height)',
  boxShadow: $['shadow.overlay.inverted'],
  transform: 'translateY(0%)',
  backgroundColor: $['color.background.surface'],
};

const fullscreenSheetStyles: React.CSSProperties = {
  ['--bottom-sheet-max-height' as string]:
    'var(--bottom-sheet-viewport-height-without-margins-and-safe-areas)',
  minHeight: 'unset',
  height: '100%',
  maxHeight: 'var(--bottom-sheet-max-height)',
  top: 'var(--sail-layer-margin-top, 0px)',
  backgroundColor: $['color.background.surface'],
};

/**
 * We don't have a good way to prioritize the resetStyles and the Bottom sheet / Fullscreen sheet styles over the inherited styles (css + style props).
 * The priority of styles should be: Bottom sheet / Fullscreen sheet styles > Reset styles > Inherited styles (style prop) > Inherited styles (css prop).
 * The only way to achieve this without modifying @sail/engine is to use the `style` prop instead of `css` to apply our reset and sheet styles.
 */
export function getBottomSheetStyles(
  bottomSheetVariant: BottomSheetVariant,
  propsStyle: React.CSSProperties | undefined,
): React.CSSProperties {
  return {
    ...(propsStyle ?? {}),
    ...resetStyles,
    ...(bottomSheetVariant === 'bottom-sheet' ? bottomSheetStyles : {}),
    ...(bottomSheetVariant === 'fullscreen-sheet' ? fullscreenSheetStyles : {}),
  };
}

export const desktopFullscreenSheetStyles = dynamic(() => {
  const {isSmallScreen} = useMediaQuery();

  if (!isSmallScreen) {
    return [];
  }

  return [
    css({
      width: 'fill',
      minHeight: 'var(--close-confirmation-dialog-height, 0px)',
      height:
        'calc(100% - var(--sail-layer-margin-top, 0px) - var(--sail-layer-margin-bottom, 0px))',
      borderRadius: '0px',
      maxHeight: 'fill',
      borderWidth: '0px',
      top: 'var(--sail-layer-margin-top, 0px)',
      boxShadow: 'none',
    }),
  ];
});

export const fullscreenAnimation = (disabled: boolean) =>
  animate.inout({
    duration: 320,
    transform: 'translateX(100%)',
    easing: BOTTOM_SHEET_ANIMATION_EASING,
    preserveEasingOnReverse: true,
    disabled,
  });

export const bottomSheetAnimation = (
  disabled: boolean,
  duration: number,
  hasChildSheets: boolean,
) =>
  toIntent([
    animate
      .transition({
        duration,
        transform: `translateY(calc(100% + var(--sail-layer-margin-bottom, 0px))) scale(0.9)`,
        easing: BOTTOM_SHEET_ANIMATION_EASING,
        preserveEasingOnReverse: true,
        disabled,
      })
      .when(hasChildSheets),
    animate.inout({
      duration,
      transform:
        'translateY(calc(100% + var(--sail-layer-margin-bottom, 0px)))',
      easing: BOTTOM_SHEET_ANIMATION_EASING,
      preserveEasingOnReverse: true,
      disabled,
    }),
  ]);
